import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { pbEngineer } from '../lib/pb';
import { useToast } from '../shared/components/ToastProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';
import { JOB_TYPE_LABELS, templateFor } from './jobTemplates';
import SignaturePad from './SignaturePad';

// Storage key for the autosaved draft (spec §4): offline mid-form work is
// never lost. Keyed per sheet id; cleared once the sheet is saved/completed.
const DRAFT_KEY = (id) => `jsdraft:${id}`;

function loadDraft(id) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Downscale an image File to a ≤900px JPEG data URL (keeps photos small
// enough for localStorage autosave + the photos JSON field).
function fileToDataUrl(file, maxSize = 900) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read image'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.72));
      };
      img.onerror = () => reject(new Error('Invalid image'));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function JobSheetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();

  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);
  const [engineerJob, setEngineerJob] = useState(null);
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState(''); // '' | 'saved' | 'error'
  const [now, setNow] = useState(Date.now());
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const notesRef = useRef(null);

  const engineer = pbEngineer.authStore.record;

  // ---- Load sheet + linked job + property history --------------------
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const rec = await pbEngineer.collection('engineer_job_sheets').getOne(id, {
          expand: 'engineer_job_id,client_id',
        });
        if (!active) return;
        setSheet(rec);

        // Rehydrate form: autosaved draft wins, else the sheet's form_data.
        // Locked sheets (completed/submitted) never rehydrate a draft — the
        // sheet content is fixed; only the office can re-open it (019).
        const lockedStatus = rec.status === 'completed' || rec.status === 'submitted';
        const draft = lockedStatus ? null : loadDraft(id);
        const parsed = (() => {
          try {
            return typeof rec.form_data === 'string' ? JSON.parse(rec.form_data) : (rec.form_data || {});
          } catch {
            return {};
          }
        })();
        setForm(draft || parsed || {});

        // Linked engineer job (for clock in/out + completion).
        if (rec.engineer_job_id) {
          pbEngineer.collection('engineer_jobs').getOne(rec.engineer_job_id, { expand: 'client_id' })
            .then((job) => { if (active) setEngineerJob(job); })
            .catch((err) => console.warn('Failed to load linked job:', err));
        }

        // Property/client history: past jobs + notes for this client
        // (rules extended for engineers in migration 016 — tenant-scoped).
        if (rec.client_id) {
          const [pastJobs, pastNotes] = await Promise.all([
            pbEngineer.collection('jobs').getFullList({
              filter: `client_id="${rec.client_id}"`,
              sort: '-scheduled_start,-date_completed',
            }).catch(() => []),
            pbEngineer.collection('notes').getFullList({
              filter: `client_id="${rec.client_id}"`,
              sort: '-created',
            }).catch(() => []),
          ]);
          if (active) {
            setHistory([
              ...pastJobs.map((j) => ({
                kind: 'job',
                date: j.date_completed || j.scheduled_start || j.created,
                text: `${j.title || 'Job'} — ${(j.status || '').replace('_', ' ')}`,
              })),
              ...pastNotes.map((n) => ({
                kind: 'note',
                date: n.created,
                text: n.body || '',
              })),
            ].slice(0, 12));
          }
        }
      } catch (err) {
        console.error('Failed to load job sheet:', err);
        if (active) notify('Could not load this job sheet', 'error');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [id, notify]);

  // ---- Autosave draft (localStorage) on every form change ------------
  useEffect(() => {
    if (!form || !sheet) return;
    // Locked sheets (completed/submitted) are read-only — never write drafts.
    if (sheet.status === 'completed' || sheet.status === 'submitted') return;
    try {
      localStorage.setItem(DRAFT_KEY(id), JSON.stringify(form));
      setSaveState('saved');
      const t = setTimeout(() => setSaveState(''), 1500);
      return () => clearTimeout(t);
    } catch {
      setSaveState('error');
    }
    return undefined;
  }, [form, id, sheet]);

  // ---- Elapsed timer while clocked in ---------------------------------
  useEffect(() => {
    if (!engineerJob?.time_on_site_start || engineerJob?.time_on_site_end) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [engineerJob?.time_on_site_start, engineerJob?.time_on_site_end]);

  const clockedIn = Boolean(engineerJob?.time_on_site_start && !engineerJob?.time_on_site_end);
  const elapsedMs = clockedIn
    ? now - new Date(engineerJob.time_on_site_start).getTime()
    : engineerJob?.time_on_site_start && engineerJob?.time_on_site_end
      ? new Date(engineerJob.time_on_site_end).getTime() - new Date(engineerJob.time_on_site_start).getTime()
      : 0;

  const formatElapsed = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  };

  // ---- Clock in/out (schema §2: time_on_site_start / time_on_site_end)
  const handleClock = async () => {
    if (!engineerJob) return;
    setSaving(true);
    try {
      if (clockedIn) {
        await pbEngineer.collection('engineer_jobs').update(engineerJob.id, {
          time_on_site_end: new Date().toISOString(),
        });
        notify('Clocked out — time on site saved', 'success');
      } else {
        await pbEngineer.collection('engineer_jobs').update(engineerJob.id, {
          time_on_site_start: new Date().toISOString(),
          status: 'in_progress',
        });
        notify('Clocked in', 'success');
      }
      const fresh = await pbEngineer.collection('engineer_jobs').getOne(engineerJob.id, { expand: 'client_id' });
      setEngineerJob(fresh);
    } catch (err) {
      console.error('Clock update failed:', err);
      notify('Could not update the clock', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Form field helpers ----------------------------------------------
  const updateForm = useCallback((patch) => {
    setForm((cur) => ({ ...(cur || {}), ...patch }));
  }, []);

  const toggleCheck = (index) => {
    const checklist = (form?.checklist || []).map((item, i) =>
      i === index ? { ...item, done: !item.done } : item);
    updateForm({ checklist });
  };

  const addMaterial = () => {
    updateForm({ materials: [...(form?.materials || []), ''] });
  };

  const updateMaterial = (index, value) => {
    const materials = (form?.materials || []).map((m, i) => (i === index ? value : m));
    updateForm({ materials });
  };

  const removeMaterial = (index) => {
    updateForm({ materials: (form?.materials || []).filter((_, i) => i !== index) });
  };

  const addPhotos = async (files) => {
    const existing = form?.photos || [];
    const room = Math.max(0, 4 - existing.length);
    const taken = Array.from(files).slice(0, room);
    if (taken.length === 0) {
      notify('Maximum 4 photos per sheet', 'error');
      return;
    }
    try {
      const urls = await Promise.all(taken.map((f) => fileToDataUrl(f)));
      updateForm({ photos: [...existing, ...urls] });
    } catch (err) {
      notify(err.message || 'Could not read photo', 'error');
    }
  };

  const removePhoto = (index) => {
    updateForm({ photos: (form?.photos || []).filter((_, i) => i !== index) });
  };

  // ---- Voice-to-text (native SpeechRecognition, no new service) --------
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      notify('Voice input is not supported in this browser', 'error');
      return;
    }
    if (listening) {
      notesRef.current?.stop?.();
      setListening(false);
      return;
    }
    const rec = new SR();
    notesRef.current = rec;
    rec.lang = 'en-GB';
    rec.interimResults = false;
    rec.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join(' ');
      updateForm({ notes: `${form?.notes || ''}${form?.notes ? ' ' : ''}${transcript}` });
    };
    rec.onend = () => setListening(false);
    rec.onerror = (event) => {
      setListening(false);
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        notify(`Voice input error: ${event.error}`, 'error');
      }
    };
    rec.start();
    setListening(true);
  };

  // ---- Save to PocketBase -----------------------------------------------
  const persist = async ({ completed = false } = {}) => {
    if (!sheet) return null;
    setSaving(true);
    try {
      const checklist = form?.checklist || [];
      const doneCount = checklist.filter((c) => c.done).length;
      const workSummary = (form?.notes || '').trim();
      const patch = {
        form_data: JSON.stringify(form || {}),
        work_summary: workSummary,
        // Top-level materials_used mirrors form_data.materials so the admin
        // card preview and the review detail agree (was only in form_data).
        materials_used: (form?.materials || []).filter(Boolean).join(', '),
        status: completed ? 'completed' : checklist.some((c) => c.done) ? 'in_progress' : sheet.status,
        ...(completed ? { completed_at: new Date().toISOString() } : {}),
      };
      const rec = await pbEngineer.collection('engineer_job_sheets').update(sheet.id, patch);
      localStorage.removeItem(DRAFT_KEY(id));
      setSheet(rec);
      return rec;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      await persist();
      notify('Job sheet saved', 'success');
    } catch (err) {
      console.error('Save failed:', err);
      notify(err.message || 'Save failed', 'error');
    }
  };

  // ---- Complete job → signature → status flips --------------------------
  const handleComplete = async () => {
    if (!engineerJob) return;
    setSignatureOpen(true);
  };

  const confirmSignature = async (signatureData) => {
    try {
      // Validate engineer authentication
      if (!engineer?.id) {
        throw new Error('Engineer not authenticated. Please log in again.');
      }
      
      // Validate required data
      if (!sheet?.client_id) {
        throw new Error('Client information missing. Cannot create signature.');
      }
      if (!engineerJob?.id) {
        throw new Error('Job information missing. Cannot create signature.');
      }
      
      const rec = await persist({ completed: true });
      // Signature row (spec §1.1): signature_data + signed_at, linked to
      // the sheet + job + client.
      await pbEngineer.collection('engineer_signatures').create({
        tenant_id: engineer?.tenant_id || rec.tenant_id,
        client_id: sheet.client_id,
        engineer_job_id: engineerJob.id,
        engineer_job_sheet_id: rec.id,
        client_name: sheet.expand?.client_id?.name || '',
        signature_data: signatureData,
        signed_at: new Date().toISOString(),
      });
      // Mark the job completed.
      await pbEngineer.collection('engineer_jobs').update(engineerJob.id, {
        status: 'completed',
        completed_at: new Date().toISOString(),
      });
      setSignatureOpen(false);
      notify('Job completed — signature saved', 'success');
      navigate('..', { relative: 'path' });
    } catch (err) {
      console.error('Completion failed:', err);
      notify(err.message || 'Could not complete the job', 'error');
      setSignatureOpen(false);
    }
  };

  // ---- Render ------------------------------------------------------------
  if (loading) return <div className="page-container"><Skeleton rows={8} /></div>;

  if (!sheet) {
    return (
      <div className="page-container">
        <EmptyState
          title="Job sheet not found"
          message="It may have been removed or you don't have access."
          action="Back to Job Sheets"
          onAction={() => navigate('..', { relative: 'path' })}
        />
      </div>
    );
  }

  const steps = ['draft', 'in_progress', 'completed', 'submitted'];
  const stepIndex = steps.indexOf(sheet.status);
  const jobType = form?.job_type || 'other';
  const checklist = form?.checklist || [];
  const doneCount = checklist.filter((c) => c.done).length;
  const checklistComplete = checklist.length > 0 && doneCount === checklist.length;
  // Locked once completed/submitted (migration 019): read-only for the
  // engineer; only the office can re-open for amendments. The captured
  // signature row is immutable regardless.
  const locked = sheet.status === 'completed' || sheet.status === 'submitted';

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{engineerJob?.title || sheet.expand?.engineer_job_id?.title || 'Job Sheet'}</h1>
          <p>
            {sheet.expand?.client_id?.name || 'Client'} · {engineerJob?.address || sheet.expand?.engineer_job_id?.address || ''}
            {jobType && jobType !== 'other' ? ` · ${JOB_TYPE_LABELS[jobType] || jobType}` : ''}
          </p>
        </div>
        <div className="header-actions">
          {locked ? (
            <span className="autosave-hint saved">
              <SafeIcon icon={Icons.Lock} size={12} />
              Locked — awaiting office review
            </span>
          ) : (
            <span className={`autosave-hint ${saveState === 'saved' ? 'saved' : ''}`}>
              <SafeIcon icon={saveState === 'saved' ? Icons.Check : Icons.Cloud} size={12} />
              {saveState === 'saved' ? 'Draft saved locally' : 'Draft autosaved on change'}
            </span>
          )}
          <button className="secondary-button" onClick={() => navigate('..', { relative: 'path' })}>
            <SafeIcon icon={Icons.ArrowLeft} size={15} />
            Back
          </button>
        </div>
      </div>

      {/* Status stepper */}
      <div className="status-stepper">
        {steps.map((step, i) => (
          <React.Fragment key={step}>
            {i > 0 && <span style={{ color: 'var(--line)', fontSize: 12 }}>→</span>}
            <span className={`step ${i < stepIndex ? 'done' : ''} ${i === stepIndex ? 'current' : ''}`}>
              <span className="step-dot">{i < stepIndex ? <SafeIcon icon={Icons.Check} size={11} /> : i + 1}</span>
              {step.replace('_', ' ')}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="sheet-form">
        {/* Clock in/out — hidden once the sheet is locked (no re-clock after completion) */}
        {engineerJob && !locked && (
          <div className="clock-card">
            <div className="clock-info">
              <span className="clock-label">{clockedIn ? 'On site since' : engineerJob.time_on_site_start ? 'Time on site' : 'Clock in when you arrive'}</span>
              <span className="elapsed">{clockedIn || engineerJob.time_on_site_start ? formatElapsed(elapsedMs) : '—'}</span>
            </div>
            <button
              className={clockedIn ? 'warning-button' : 'primary-button'}
              onClick={handleClock}
              disabled={saving}
            >
              <SafeIcon icon={clockedIn ? Icons.LogOut : Icons.LogIn} size={15} />
              {clockedIn ? 'Clock Out' : 'Clock In'}
            </button>
          </div>
        )}

        {/* Checklist */}
        <div className="detail-panel">
          <div className="section-header">
            <h3>Checklist ({doneCount}/{checklist.length})</h3>
            {checklistComplete && (
              <span className="stock-badge in-stock"><SafeIcon icon={Icons.Check} size={11} /> Complete</span>
            )}
          </div>
          <div className="checklist">
            {checklist.length === 0 && <p className="form-hint">No checklist for this job type.</p>}
            {checklist.map((item, index) => (
              <div key={index} className={`check-item ${item.done ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  id={`check-${index}`}
                  checked={Boolean(item.done)}
                  disabled={locked}
                  onChange={() => toggleCheck(index)}
                />
                <label htmlFor={`check-${index}`}>{item.label}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Work notes + voice */}
        <div className="detail-panel">
          <div className="section-header">
            <h3>Work notes</h3>
            {!locked && (
              <div className="voice-note">
                <button className={`mic-button ${listening ? 'listening' : ''}`} onClick={toggleVoice}>
                  <SafeIcon icon={Icons.Mic} size={14} />
                  {listening ? 'Listening…' : 'Dictate'}
                </button>
              </div>
            )}
          </div>
          <div className="form-field">
            <textarea
              placeholder="Describe the work completed, readings taken, defects found…"
              value={form?.notes || ''}
              disabled={locked}
              onChange={(e) => updateForm({ notes: e.target.value })}
              style={{ minHeight: 120 }}
            />
          </div>
          <p className="voice-hint">Dictation uses your browser's built-in speech recognition — no data leaves your device.</p>
        </div>

        {/* Materials */}
        <div className="detail-panel">
          <div className="section-header">
            <h3>Materials used</h3>
            {!locked && (
              <button className="text-button" onClick={addMaterial}>
                <SafeIcon icon={Icons.Plus} size={13} /> Add material
              </button>
            )}
          </div>
          <div className="materials-list">
            {(form?.materials || []).map((material, index) => (
              <div key={index} className="material-row">
                <input
                  type="text"
                  placeholder="e.g. 15mm compression valve × 2"
                  value={material}
                  disabled={locked}
                  onChange={(e) => updateMaterial(index, e.target.value)}
                />
                {!locked && (
                  <button className="remove-material" onClick={() => removeMaterial(index)} aria-label="Remove material">×</button>
                )}
              </div>
            ))}
            {(form?.materials || []).length === 0 && <p className="form-hint">No materials logged yet.</p>}
          </div>
        </div>

        {/* Photos */}
        <div className="detail-panel">
          <div className="section-header">
            <h3>Photos ({form?.photos?.length || 0}/4)</h3>
            {!locked && (
              <label className="photo-upload">
                <SafeIcon icon={Icons.Camera} size={14} />
                Add photos
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  hidden
                  onChange={(e) => { if (e.target.files?.length) addPhotos(e.target.files); e.target.value = ''; }}
                />
              </label>
            )}
          </div>
          {form?.photos?.length > 0 && (
            <div className="photo-grid">
              {form.photos.map((photo, index) => (
                <div key={index} className="photo-thumb">
                  <img src={photo} alt={`Site photo ${index + 1}`} />
                  {!locked && (
                    <button className="photo-remove" onClick={() => removePhoto(index)} aria-label="Remove photo">×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Property / client history */}
        <div className="detail-panel">
          <div className="section-header">
            <h3>Property & client history</h3>
            <SafeIcon icon={Icons.Clock} size={14} />
          </div>
          {history.length === 0 ? (
            <p className="form-hint">No previous visits or notes on file for this client.</p>
          ) : (
            <div className="property-history">
              {history.map((item, index) => (
                <div key={index} className="history-item">
                  <span className="history-date">
                    {item.kind === 'job' ? 'Job' : 'Note'} · {new Date(item.date).toLocaleDateString()}
                  </span>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions — locked sheets show a notice, not edit buttons */}
        {locked ? (
          <div className="form-actions">
            <p className="form-hint" style={{ margin: 0 }}>
              <SafeIcon icon={Icons.Lock} size={13} /> This job sheet is completed and locked.
              Contact the office if amendments are needed — the signed record is never changed.
            </p>
          </div>
        ) : (
          <div className="form-actions">
            <button className="secondary-button" onClick={handleSave} disabled={saving}>
              <SafeIcon icon={Icons.Cloud} size={15} />
              Save Sheet
            </button>
            <button
              className="primary-button"
              onClick={handleComplete}
              disabled={saving || !checklistComplete}
              title={!checklistComplete ? 'Complete the checklist to finish the job' : 'Capture client signature and complete'}
            >
              <SafeIcon icon={Icons.Check} size={16} />
              Complete Job & Capture Signature
            </button>
            {!checklistComplete && (
              <p className="form-hint">Complete all checklist items before finishing the job.</p>
            )}
          </div>
        )}
      </div>

      {signatureOpen && (
        <SignaturePad
          clientName={sheet.expand?.client_id?.name || 'Client'}
          onConfirm={confirmSignature}
          onCancel={() => setSignatureOpen(false)}
        />
      )}
    </div>
  );
}
